import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AllergyManagement = ({ allergies = [] }) => {
  const [showAddForm, setShowAddForm] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Severe':
        return 'bg-destructive text-destructive-foreground';
      case 'Moderate':
        return 'bg-warning text-warning-foreground';
      case 'Mild':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Drug':
        return 'Pill';
      case 'Food':
        return 'Apple';
      case 'Environmental':
        return 'Wind';
      default:
        return 'AlertTriangle';
    }
  };

  if (allergies?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
          <Icon name="AlertTriangle" size={32} color="var(--color-muted-foreground)" />
        </div>
        <p className="text-base font-medium text-foreground mb-2">No Known Allergies</p>
        <p className="text-sm text-muted-foreground mb-4">Patient allergy information will appear here</p>
        <Button variant="default" iconName="Plus" iconPosition="left">
          Add Allergy
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Known Allergies</h3>
          <span className="inline-flex items-center px-3 py-1 bg-destructive/10 text-destructive rounded-full text-xs font-medium">
            {allergies?.filter(a => a?.severity === 'Severe')?.length} Severe
          </span>
        </div>
        <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
          Add Allergy
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {allergies?.map((allergy) => (
          <div
            key={allergy?.id}
            className="bg-card border-2 border-destructive/20 rounded-lg p-4 md:p-5 transition-smooth hover:shadow-elevation-2"
          >
            <div className="flex items-start gap-3 md:gap-4">
              <div className="flex-shrink-0 text-destructive">
                <Icon name={getTypeIcon(allergy?.type)} size={24} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <h4 className="text-base md:text-lg font-semibold text-foreground mb-1">
                      {allergy?.allergen}
                    </h4>
                    <span className="text-xs md:text-sm text-muted-foreground">{allergy?.type}</span>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getSeverityColor(allergy?.severity)}`}>
                    {allergy?.severity}
                  </span>
                </div>
                
                {allergy?.reaction && (
                  <div className="bg-destructive/5 rounded-lg p-3 mb-3">
                    <p className="text-sm font-medium text-foreground mb-1">Reaction:</p>
                    <p className="text-sm text-muted-foreground">{allergy?.reaction}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-muted-foreground">
                  {allergy?.onsetDate && (
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={14} />
                      <span>Onset: {allergy?.onsetDate}</span>
                    </div>
                  )}
                  {allergy?.verifiedBy && (
                    <div className="flex items-center gap-2">
                      <Icon name="User" size={14} />
                      <span>Verified by: {allergy?.verifiedBy}</span>
                    </div>
                  )}
                </div>
                
                {allergy?.notes && (
                  <p className="text-xs text-muted-foreground mt-3 line-clamp-2">
                    Note: {allergy?.notes}
                  </p>
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                <Button variant="ghost" size="sm" iconName="Edit" />
                <Button variant="ghost" size="sm" iconName="Trash2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllergyManagement;